import React from 'react';
import useSEO from "./useSEO";

const ConstructionStockAudit = () => {
  useSEO({
    title: "Construction Stock Audit | Construction Material Audit Services in Hyderabad | Site Inventory Verification Experts",
    description:
      "Ensure material accountability and project efficiency with professional construction stock audit services in Hyderabad. We verify cement, steel, sand, and other construction materials to prevent losses, improve cost control, and ensure compliance.",
    keywords:
      "construction stock audit, site material audit, construction audit Hyderabad, building material verification, civil inventory audit, real estate audit, cement and steel stock verification, infrastructure project audit, contractor material audit, construction warehouse audit, project inventory audit, construction cost control audit, on-site stock verification",
    ogTitle: "Construction Stock Audit - Hyderabad Experts",
    ogDescription:
      "Professional construction material audits covering cement, steel, sand, and project site inventory to improve cost efficiency and transparency.",
    ogImage: "img/construction-stock-audit.png",
    twitterTitle: "Construction Stock Audit - Hyderabad Experts",
    twitterDescription:
      "Accurate construction stock audits for builders and contractors — control costs, prevent wastage, and ensure site accountability.",
    twitterImage: "img/construction-stock-audit.png"
  });

  return (
    <div>
      <>
        {/* Section 1 - Hero */}
        <section className="pt-lg-8 pt-xl-9">
          <div className="container position-relative pt-4 pt-lg-0">
            <div className="row align-items-center">
              {/* Left - Text */}
              <div className="col-md-7 col-xl-6 mb-7 mb-md-0">
                <h1 className="mb-4 text-primary">Construction Stock Audit</h1>
                <p className="mb-4">
                  A <b>Construction Stock Audit</b> ensures that all materials
                  — from cement, steel, and aggregates to tiles, paint, and
                  electrical fittings — are accurately recorded and efficiently
                  managed at every project site. Our audits help builders,
                  contractors, and developers reduce material wastage, control
                  project costs, and enhance transparency.
                </p>
                <p>
                  <b>Why Construction Material Auditing is Important:</b><br />
                  In the construction industry, mismanagement of materials often
                  leads to project delays and cost overruns. Our audit services
                  help maintain accurate site inventory, prevent pilferage, and
                  improve procurement efficiency.
                </p>
              </div>

              {/* Right - Image */}
              <div className="col-md-5 ms-auto text-center">
                <img
                  src="imgs/construction-stock-audit.jpg"
                  alt="Construction Stock Audit"
                  className="rounded-3 shadow-sm w-100"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Section 2 - Audit Process */}
        <section className="overflow-hidden py-5 bg-light border-top">
          <div className="container">
            <div className="row align-items-center">
              {/* Left - Image */}
              <div className="col-lg-6 text-center mb-5 mb-lg-0">
                <div className="pe-lg-6 pe-xl-8">
                  <img
                    src="imgs/construction-stock-audit-process.jpg"
                    alt="Construction Audit Process"
                    className="rounded-4 shadow-sm w-100"
                  />
                </div>
              </div>

              {/* Right - Content */}
              <div className="col-lg-6 ms-auto">
                <h2 className="mb-lg-4">Our Construction Audit Process</h2>
                <hr className="my-4 my-lg-5" />

                <ul className="list-group list-group-borderless">
                  <li className="list-group-item d-flex">
                    <i className="bi bi-patch-check text-primary me-2" />
                    <b>Material Receipt & Issue Verification</b>
                  </li>
                  <p>
                    Verification of all incoming and outgoing construction
                    materials at the site, matched against purchase orders and
                    material issue slips.
                  </p>

                  <li className="list-group-item d-flex">
                    <i className="bi bi-patch-check text-primary me-2" />
                    <b>Physical Stock Verification</b>
                  </li>
                  <p>
                    On-site counting and measurement of cement, sand, steel,
                    aggregates, and consumables to ensure accuracy with books.
                  </p>

                  <li className="list-group-item d-flex">
                    <i className="bi bi-patch-check text-primary me-2" />
                    <b>Wastage & Consumption Analysis</b>
                  </li>
                  <p>
                    Analysis of actual material usage against project estimates
                    to identify wastage, overconsumption, or leakages.
                  </p>

                  <li className="list-group-item d-flex">
                    <i className="bi bi-patch-check text-primary me-2" />
                    <b>Vendor & Subcontractor Reconciliation</b>
                  </li>
                  <p>
                    Validation of materials supplied by vendors or used by
                    subcontractors against work orders and bills.
                  </p>

                  <li className="list-group-item d-flex">
                    <i className="bi bi-patch-check text-primary me-2" />
                    <b>Asset & Tool Audit</b>
                  </li>
                  <p>
                    Verification of construction tools, machinery, and
                    equipment — ensuring proper tagging, tracking, and
                    maintenance.
                  </p>

                  <li className="list-group-item d-flex">
                    <i className="bi bi-patch-check text-primary me-2" />
                    <b>Inventory Valuation & Cost Analysis</b>
                  </li>
                  <p>
                    Ensuring correct valuation of materials and linking them to
                    project costs for accurate financial reporting.
                  </p>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Section 3 - Benefits */}
        <section className="py-5">
          <div className="container">
            <div className="text-center mb-5">
              <h2 className="text-primary">Benefits of Construction Stock Audit</h2>
              <p className="lead">
                Strengthen cost control, operational efficiency, and material
                accountability across all project sites.
              </p>
            </div>

            <div className="row text-center">
              <div className="col-md-4 mb-4">
                <i className="bi bi-box-seam text-success fs-3 mb-2"></i>
                <h5>Material Accountability</h5>
                <p>
                  Ensure every bag of cement, ton of steel, and load of sand is
                  properly tracked and accounted for.
                </p>
              </div>

              <div className="col-md-4 mb-4">
                <i className="bi bi-graph-up text-success fs-3 mb-2"></i>
                <h5>Cost Control</h5>
                <p>
                  Identify leakages, theft, or wastage to reduce overall
                  construction expenses and improve profitability.
                </p>
              </div>

              <div className="col-md-4 mb-4">
                <i className="bi bi-cash-stack text-success fs-3 mb-2"></i>
                <h5>Budget Optimization</h5>
                <p>
                  Align material usage with project estimates and timelines for
                  better budget forecasting and utilization.
                </p>
              </div>

              <div className="col-md-4 mb-4">
                <i className="bi bi-shield-check text-success fs-3 mb-2"></i>
                <h5>Compliance & Transparency</h5>
                <p>
                  Maintain audit-ready reports to meet internal, client, and
                  regulatory requirements with transparency.
                </p>
              </div>

              <div className="col-md-4 mb-4">
                <i className="bi bi-tools text-success fs-3 mb-2"></i>
                <h5>Equipment Management</h5>
                <p>
                  Keep construction assets and tools in check with accurate
                  location-based tagging and maintenance tracking.
                </p>
              </div>

              <div className="col-md-4 mb-4">
                <i className="bi bi-lightbulb text-success fs-3 mb-2"></i>
                <h5>Project Efficiency</h5>
                <p>
                  Enable smarter planning, timely material availability, and
                  uninterrupted workflow for construction teams.
                </p>
              </div>
            </div>
          </div>
        </section>
      </>
    </div>
  );
};

export default ConstructionStockAudit;
